import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { MessageSquare, ArrowLeft, CheckCircle2, ShieldCheck, XCircle } from "lucide-react";

/**
 * Public self-service SMS opt-in form (no login required).
 * Required by Twilio toll-free verification.
 *
 * Compliance framing (Twilio error 30505 & 30509):
 *   - Opt-in is fully OPTIONAL and voluntary, not a condition of employment
 *     or use of any Canales service.
 *   - Consent checkbox is UNCHECKED by default.
 *   - User can explicitly DECLINE and still leave the page — "No thanks"
 *     button navigates away without submitting.
 *   - Language reads "Would you also like to receive…" instead of "You must".
 *   - Message frequency, message & data rates, STOP/HELP disclosures visible
 *     BEFORE submission, matching the exact CTIA-recommended wording.
 */
export default function SmsOptInPage() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [store, setStore] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!consent) {
      setErr("Please check the consent box if you would like to opt in — this step is voluntary.");
      return;
    }
    try {
      setBusy(true);
      await axios.post(`${API}/sms/optin`, { name, phone, store, consent });
      setDone(true);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-[#0B1220] text-white py-10 px-6">
        <div className="max-w-2xl mx-auto">
          <Link
            to="/sms-terms"
            className="inline-flex items-center gap-1.5 text-xs text-stone-300 hover:text-white mb-4"
            data-testid="back-to-sms-terms"
          >
            <ArrowLeft size={12} /> SMS Program Terms
          </Link>
          <div className="canales-overline text-stone-300 flex items-center gap-2">
            <MessageSquare size={12} /> Optional · SMS Notifications
          </div>
          <h1 className="font-display text-3xl md:text-5xl mt-2 leading-[1.05]">
            Would you also like to receive SMS notifications?
          </h1>
          <div className="mt-4 rounded-lg bg-amber-400 text-stone-900 px-4 py-3 max-w-xl">
            <p className="text-sm md:text-[15px] font-semibold leading-snug">
              You do <u>not</u> need this to use Canales Auditor.
            </p>
            <p className="text-xs md:text-sm mt-1 leading-relaxed">
              The web app is fully usable without opting in. This SMS form is a
              separate, 100% optional add-on. You can close this page or click
              <strong> &ldquo;No thanks&rdquo;</strong> at any time — you will
              still be able to log in, run audits, view reports and do everything
              the platform does.
            </p>
          </div>
          <p className="text-stone-300 mt-4 max-w-xl text-sm md:text-base">
            This sign-up is <strong className="text-white">completely voluntary</strong>.
            Employees of Mueblería Canales (Canales Franchise LLC) may choose to
            receive operational audit alerts, task assignments and follow-up
            reminders by SMS. SMS notifications are <em>not</em> a condition of
            employment, purchase, or use of any Canales service.
          </p>
          <p className="text-stone-300 mt-3 max-w-xl text-xs md:text-sm">
            💬 Frequency: about 1–20 messages per month. Message &amp; data rates
            may apply. Reply <strong>STOP</strong> to unsubscribe at any time,
            <strong> HELP</strong> for help. See{" "}
            <Link to="/sms-terms" className="underline">SMS Program Terms</Link>{" "}
            and <Link to="/privacy" className="underline">Privacy Policy</Link>.
          </p>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-10">
        {done ? (
          <div
            className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-8 text-center"
            data-testid="optin-success"
          >
            <CheckCircle2 className="mx-auto text-emerald-600" size={44} />
            <h2 className="font-display text-2xl mt-4 text-stone-900">You&apos;re opted in!</h2>
            <p className="text-stone-700 text-sm mt-2 max-w-md mx-auto">
              Your request was received. An administrator will confirm your number
              and activate your notification categories. You can reply{" "}
              <strong>STOP</strong> to any message to unsubscribe at any time, or{" "}
              <strong>HELP</strong> for help.
            </p>
            <Link to="/sms-terms" className="inline-block mt-5 text-sm underline text-stone-700">
              Read the full SMS Program Terms
            </Link>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="rounded-2xl border border-stone-200 bg-white p-6 md:p-8 space-y-5"
            data-testid="optin-form"
          >
            {/* Voluntary banner — extra explicit for Twilio compliance */}
            <div
              className="rounded-lg border border-blue-200 bg-blue-50/60 p-3 text-[12.5px] text-blue-900 leading-relaxed"
              data-testid="optin-voluntary-banner"
            >
              <strong>This step is optional.</strong> If you do not want SMS
              notifications, simply close this page or click{" "}
              <em>&ldquo;No thanks — take me back&rdquo;</em> below. There is no penalty for
              declining, and your access to Canales services is unaffected.
            </div>

            <div>
              <label className="canales-overline">Full name *</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Antonio Canales"
                required
                className="mt-1 h-11"
                data-testid="optin-name-input"
              />
            </div>
            <div>
              <label className="canales-overline">Mobile phone number *</label>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 817 555 0100"
                required
                className="mt-1 h-11"
                data-testid="optin-phone-input"
              />
              <p className="text-xs text-stone-500 mt-1">
                Use international format with country code (e.g. +1 for USA).
              </p>
            </div>
            <div>
              <label className="canales-overline">Store / position (optional)</label>
              <Input
                value={store}
                onChange={(e) => setStore(e.target.value)}
                placeholder="e.g. FBGS Jefferson Store · Manager"
                className="mt-1 h-11"
                data-testid="optin-store-input"
              />
            </div>

            {/* Sample messages — visible so Twilio reviewers and users see
                exactly what they'll receive before consenting. */}
            <div
              className="rounded-lg border border-stone-200 bg-stone-50 p-4 space-y-2"
              data-testid="optin-sample-messages"
            >
              <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                Examples of the messages you would receive
              </p>
              <pre className="whitespace-pre-wrap font-mono text-[11.5px] leading-relaxed bg-white border border-stone-200 rounded p-2 text-stone-700">
{`[Canales · AUD-2026-523] Reyna LaGranPlaza Store
[Multa] Image / Uniform: Is the sales rep wearing the tucked-in blue polo…
Auditor: Antonio Canales`}
              </pre>
              <pre className="whitespace-pre-wrap font-mono text-[11.5px] leading-relaxed bg-white border border-stone-200 rounded p-2 text-stone-700">
{`[Canales · TO DO assigned]
Store: Guzman Northridge Hills
Description: Replace broken Wall Art display tag (aisle 3)
Owner: Antonio Luna · Assigned by: Antonio Canales`}
              </pre>
              <pre className="whitespace-pre-wrap font-mono text-[11.5px] leading-relaxed bg-white border border-stone-200 rounded p-2 text-stone-700">
{`[Canales · 24 h Reminder]
Store: Reyna LaGranPlaza Store
Description: Catalog disorder in financing wall — not yet started.
Status: to_do · Owner: David Casique`}
              </pre>
              <p className="text-[11px] text-stone-500 leading-relaxed">
                These are internal operational messages only — no marketing,
                promotions, or third-party content is ever sent.
              </p>
            </div>

            <label
              className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50/50 p-4 cursor-pointer"
              data-testid="optin-consent-label"
            >
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-[#0B1220]"
                data-testid="optin-consent-checkbox"
              />
              <span className="text-[13px] leading-relaxed text-stone-700">
                <strong>Yes, I would like to opt in.</strong> I expressly consent
                to receive operational SMS text notifications (audit alerts,
                task assignments, follow-up reminders) from{" "}
                <strong>Mueblería Canales / Canales Franchise LLC</strong> at the
                mobile number provided above. Message frequency varies (typically
                1–20 messages per month). Message &amp; data rates may apply. Reply{" "}
                <strong>STOP</strong> to unsubscribe at any time or{" "}
                <strong>HELP</strong> for help. Consent is not a condition of
                employment or purchase. I have read the{" "}
                <Link to="/sms-terms" className="underline font-medium">SMS Terms</Link>{" "}
                and{" "}
                <Link to="/privacy" className="underline font-medium">Privacy Policy</Link>.
              </span>
            </label>

            {err && (
              <div
                className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3"
                data-testid="optin-error"
              >
                {err}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                type="submit"
                disabled={busy}
                className="flex-1 h-11 brand-button"
                data-testid="optin-submit-button"
              >
                {busy ? "Submitting…" : "Yes, opt me in (this is optional)"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/")}
                className="flex-1 h-11 gap-1.5"
                data-testid="optin-decline-button"
                disabled={busy}
              >
                <XCircle size={14} />
                No thanks — I&apos;ll skip SMS
              </Button>
            </div>

            <p className="text-[11px] text-stone-500 text-center">
              Declining is fine — the Canales Auditor app keeps working normally
              either way. Your phone number is never sold, rented or shared with
              third parties.
            </p>
          </form>
        )}

        <div className="text-xs text-stone-500 pt-6 mt-6 border-t border-stone-200 flex items-center gap-1.5">
          <ShieldCheck size={12} /> Canales Franchise LLC ·{" "}
          <Link to="/sms-terms" className="underline">SMS Terms</Link> ·{" "}
          <Link to="/privacy" className="underline">Privacy</Link> ·{" "}
          <Link to="/terms" className="underline">Terms</Link>
        </div>
      </main>
    </div>
  );
}

